import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';

const SECTIONS = [
  {
    title: 'Reporter Database',
    subtitle: 'Find the right journalists instantly',
    desc: 'Stop cold-researching every pitch. PitchFriendly indexes tens of thousands of reporters across every beat — with their contact info, publication, recent articles, and coverage history all in one place.',
    points: [
      'Search by beat, publication, location, or keyword',
      'Auto-enriched contact details (email, phone, social)',
      'Recent article history so you know what they\'re covering',
      'Save reporters to press lists for future campaigns',
    ],
    color: 'blue',
  },
  {
    title: 'Outreach Engine',
    subtitle: 'Send personalized pitches at scale',
    desc: 'Mass emails feel like spam. PitchFriendly\'s outreach engine lets you personalize every message at scale — with merge fields, per-reporter customization, and staggered delivery that mimics human sending.',
    points: [
      'Mail merge with contact-specific custom fields',
      'Staggered send to avoid spam filters',
      'Gmail and IMAP/SMTP integration',
      'Automatic reply detection and thread tracking',
    ],
    color: 'indigo',
  },
  {
    title: 'PR CRM',
    subtitle: 'Never lose the thread with a reporter',
    desc: 'Manage every reporter relationship in one place. Track conversation history, notes, and contact details. Know instantly which reporters you\'ve worked with, when, and what was covered.',
    points: [
      'Full conversation history per contact',
      'Notes and custom fields for relationship context',
      'Team-wide visibility into reporter relationships',
      'Import existing contacts from CSV',
    ],
    color: 'violet',
  },
  {
    title: 'Coverage Tracking',
    subtitle: 'Log hits and prove ROI automatically',
    desc: 'When coverage lands, log it in seconds. Track article titles, publication names, URLs, and dates. Generate client reports showing the ROI of your PR program.',
    points: [
      'Log coverage hits with article metadata',
      'Link coverage back to the pitch that got it',
      'Per-client coverage reports',
      'Trends over time with analytics',
    ],
    color: 'emerald',
  },
  {
    title: 'AI-Powered Tools',
    subtitle: 'Let AI do the heavy lifting',
    desc: 'PitchFriendly\'s AI analyzes reporter beats and drafts pitches tailored to each journalist — so your team spends less time writing and more time landing coverage.',
    points: [
      'AI pitch drafts based on reporter\'s recent coverage',
      'Beat relevance scoring before you pitch',
      'Subject line suggestions to improve open rates',
      'Reporter enrichment powered by web scraping + AI',
    ],
    color: 'amber',
  },
  {
    title: 'Team & Client Management',
    subtitle: 'Built for agencies, not just solo pros',
    desc: 'Manage multiple PR clients from a single workspace. Each client gets their own contacts, press lists, and coverage tracking — all under one roof.',
    points: [
      'Multi-client workspace with role-based access',
      'Per-client dashboards and reporting',
      'Team member activity tracking',
      'Slack notifications for key events',
    ],
    color: 'rose',
  },
];

const COLOR_MAP: Record<string, { bg: string; text: string; icon: string }> = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-600' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'text-violet-600' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-600' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-700', icon: 'text-rose-600' },
};

export default function FeaturesPage() {
  useEffect(() => {
    document.title = 'Features — PitchFriendly';
  }, []);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="bg-white border-b border-gray-100 py-16 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Everything PR, in one tool
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed">
            PitchFriendly replaces the patchwork of spreadsheets, email clients, and databases that most PR teams use. Here's what you get.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/register" className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors">
              Start free
            </Link>
            <Link to="/pricing" className="px-6 py-3 bg-gray-100 text-gray-900 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
              View pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Feature sections */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-20">
          {SECTIONS.map((s, i) => {
            const colors = COLOR_MAP[s.color] ?? COLOR_MAP.blue;
            return (
              <div key={s.title} className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                <div className="flex-1">
                  <span className={`inline-block text-xs font-semibold uppercase tracking-widest ${colors.text} mb-3`}>{s.subtitle}</span>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{s.title}</h2>
                  <p className="text-gray-500 leading-relaxed mb-6">{s.desc}</p>
                  <ul className="space-y-3">
                    {s.points.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`flex-1 ${colors.bg} rounded-2xl p-10 flex items-center justify-center min-h-[240px]`}>
                  <div className="w-full max-w-sm">
                    <div className={`text-xs font-semibold uppercase tracking-widest ${colors.text} mb-3`}>{s.title}</div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((n) => (
                        <div key={n} className="bg-white/70 rounded-lg h-10 flex items-center px-4">
                          <div className="w-1/3 h-2 bg-gray-200 rounded" />
                          <div className="ml-auto w-1/4 h-2 bg-gray-100 rounded" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-950 text-white py-20 text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold mb-4">See it in action</h2>
          <p className="text-gray-400 mb-8">Start your free account and explore every feature — no credit card required.</p>
          <Link to="/register" className="inline-block px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors">
            Get started free
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
